import React, { useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
  Chip,
  Popover,
  Radio,
  FormControlLabel,
} from "@mui/material";
import {
  Button,
  Iconography,
  Toggle,
  ToggleSizes,
  InlineEditor,
} from "@hid-galaxy-ui/galaxy-react";
import IToolBarConfig from "@hid-galaxy-ui/galaxy-react/components/InlineEditor/IToolbarConfig";
import { FeatureTechnologyOption } from "../../../../../../redux/slices/feature-management/Types";
import "../Modals.scss";
import "./FeatureModal.scss";

export type FeatureSupportRow = {
  technology: string;
  requirement: string[];
  support: string;
};

export type FeatureFormData = {
  id?: string;
  name: string;
  categoryId: number;
  implementationScope: string;
  usp: boolean;
  status: boolean;
  highLevelScenario: string;
  requirementDescription: string;
  details: FeatureSupportRow[];
};

type Props = {
  open: boolean;
  mode: "add" | "edit";
  categories: Array<{ id: number; name: string }>;
  technologyOptions: FeatureTechnologyOption[];
  requirementOptions: string[];
  initialData?: FeatureFormData | null;
  onClose: () => void;
  onSubmit: (data: FeatureFormData) => void;
};

const yearOptions = ["2026", "2027", "2028"];
const quarterOptions = ["Q1", "Q2", "Q3", "Q4"];

const editorToolBarConfig: IToolBarConfig = {
  headingConfig: { isDisplayHeading: true, headings: [false, 1, 2, 3, 4, 5, 6] },
  fontStyleConfig: {
    isDisplayBold: true,
    isDisplayItalic: true,
    isDisplayUnderline: true,
    isDisplayStrike: true,
  },
  indentConfig: {
    isDisplayOrdered: true,
    isDisplayBullet: true,
    isDisplayIndent: false,
  },
  colorConfig: { isDisplayBackground: false, isDisplayColor: false },
  alignmentConfig: {
    isDisplayJustify: false,
    isDisplayCenter: false,
    isDisplayRight: false,
  },
  miscConfig: {
    isDisplayImage: false,
    isDisplayLink: false,
    isDisplayCodeblock: false,
    isDisplayBlockquote: false,
  },
  buttonConfig: { buttonLabel: "", displayPostButton: false, isRequired: false },
};

const FeatureModal: React.FC<Props> = ({
  open,
  mode,
  categories,
  technologyOptions,
  requirementOptions,
  initialData,
  onClose,
  onSubmit,
}) => {
  const buildEmptyFormData = React.useCallback((): FeatureFormData => {
    return {
      name: "",
      categoryId: 0,
      implementationScope: "",
      usp: true,
      status: true,
      highLevelScenario: "",
      requirementDescription: "",
      details: technologyOptions.map((technology) => ({
        technology: technology.name,
        requirement: [],
        support: "",
      })),
    };
  }, [technologyOptions]);

  const normalizeDetails = React.useCallback(
    (details: FeatureSupportRow[]): FeatureSupportRow[] => {
      const incomingByTechnology = new Map(
        details.map((item) => [item.technology, item] as const)
      );
      const apiTechnologyNames = new Set(
        technologyOptions.map((technology) => technology.name)
      );

      const apiRows = technologyOptions.map((technology) => {
        const matched = incomingByTechnology.get(technology.name);
        return (
          matched || {
            technology: technology.name,
            requirement: [],
            support: "",
          }
        );
      });

      const legacyRows = details.filter(
        (item) => !apiTechnologyNames.has(item.technology)
      );

      return [...apiRows, ...legacyRows];
    },
    [technologyOptions]
  );

  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState<FeatureFormData>(buildEmptyFormData());
  const [supportAnchorEl, setSupportAnchorEl] = useState<HTMLElement | null>(null);
  const [supportEditingRow, setSupportEditingRow] = useState<number | null>(null);
  const [supportType, setSupportType] = useState<"supported" | "not-supported" | "custom">(
    "supported"
  );
  const [customMode, setCustomMode] = useState<"year" | "full-date">("year");
  const [customYear, setCustomYear] = useState("");
  const [customQuarter, setCustomQuarter] = useState("");
  const [customFullDate, setCustomFullDate] = useState("");
  const SelectIcon = (props: any) => (
    <Iconography icon="angleDown" className={`feature-select-icon ${props.className || ""}`} />
  );

  React.useEffect(() => {
    if (!open) return;

    setActiveTab(0);
    if (initialData) {
      setFormData({
        ...initialData,
        details: normalizeDetails(initialData.details || []),
      });
      return;
    }

    setFormData(buildEmptyFormData());
  }, [open, initialData, buildEmptyFormData, normalizeDetails, technologyOptions]);

  const isFeatureTabValid = useMemo(
    () =>
      formData.name.trim().length > 0 &&
      formData.categoryId > 0 &&
      formData.implementationScope.trim().length > 0,
    [formData.name, formData.categoryId, formData.implementationScope]
  );

  const isTechnologyTabValid = useMemo(
    () =>
      formData.details.every((item) => {
        if (item.support.trim().length === 0) return false;
        const supportLower = item.support.trim().toLowerCase();
        // Requirements required only for Supported; optional for Not-Supported and Custom (dates, year, quarter).
        if (supportLower === "supported") return item.requirement.length > 0;
        return true;
      }),
    [formData.details]
  );

  const isDescriptionTabValid = useMemo(
    () =>
      stripHtml(formData.highLevelScenario).trim().length > 0 &&
      stripHtml(formData.requirementDescription).trim().length > 0,
    [formData.highLevelScenario, formData.requirementDescription]
  );

  const isFormValid = isFeatureTabValid && isTechnologyTabValid && isDescriptionTabValid;

  const handleSupportRowChange = (
    index: number,
    field: "requirement" | "support",
    value: string[] | string
  ) => {
    setFormData((prev) => ({
      ...prev,
      details: prev.details.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      ),
    }));
  };

  const handleSubmit = () => {
    if (!isFormValid) return;
    onSubmit(formData);
    onClose();
  };

  const openSupportSelector = (
    event: React.MouseEvent<HTMLElement>,
    rowIndex: number,
    currentValue: string
  ) => {
    setSupportAnchorEl(event.currentTarget);
    setSupportEditingRow(rowIndex);

    const lower = currentValue.toLowerCase();
    if (lower === "supported") {
      setSupportType("supported");
      return;
    }
    if (lower === "not-supported" || lower === "not supported") {
      setSupportType("not-supported");
      return;
    }

    setSupportType("custom");
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(currentValue)) {
      setCustomMode("full-date");
      setCustomFullDate(convertDisplayDateToInputDate(currentValue));
      return;
    }
    const parts = currentValue.split(" ");
    if (parts.length === 2 && quarterOptions.includes(parts[0])) {
      setCustomMode("year");
      setCustomQuarter(parts[0]);
      setCustomYear(parts[1]);
      return;
    }
    if (yearOptions.includes(currentValue)) {
      setCustomMode("year");
      setCustomYear(currentValue);
      setCustomQuarter("");
      return;
    }
    setCustomMode("year");
    setCustomYear("");
    setCustomQuarter("");
  };

  const closeSupportSelector = () => {
    setSupportAnchorEl(null);
    setSupportEditingRow(null);
  };

  const applySupportValue = React.useCallback(() => {
    if (supportEditingRow === null) return;
    let value = "";
    if (supportType === "supported") value = "Supported";
    if (supportType === "not-supported") value = "Not-Supported";
    if (supportType === "custom") {
      if (customMode === "full-date") {
        value = customFullDate ? formatInputDateToDisplayDate(customFullDate) : "";
      } else {
        value = customQuarter ? `${customQuarter} ${customYear}` : customYear;
      }
    }
    handleSupportRowChange(supportEditingRow, "support", value);
  }, [
    supportEditingRow,
    supportType,
    customMode,
    customFullDate,
    customQuarter,
    customYear,
  ]);

  React.useEffect(() => {
    applySupportValue();
  }, [applySupportValue]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      className="requirement-modal feature-modal"
      PaperProps={{
        sx: { borderRadius: 2, boxShadow: "0px 12px 24px rgba(0,0,0,0.12)" },
      }}
    >
      <DialogTitle className="feature-modal-title">
        <Typography className="feature-modal-breadcrumb">Validation Management</Typography>
        <Typography className="feature-modal-heading">
          {mode === "edit" ? "Edit Feature" : "Add New Feature"}
        </Typography>
        <Typography className="feature-modal-subtitle">
          Configure a new feature with validation category
        </Typography>
        <IconButton aria-label="close" onClick={onClose} className="close-button">
          <Iconography icon="xmark" />
        </IconButton>
      </DialogTitle>

      <Box className="modal-tabs">
        <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)}>
          <Tab label="Feature" />
          <Tab label="Technology Support" />
          <Tab label="Description" />
        </Tabs>
      </Box>

      <DialogContent>
        {activeTab === 0 && (
          <div className="feature-tab-content">
            <div className="form-field">
              <label>
                Feature Name <span className="required">*</span>
              </label>
              <TextField
                fullWidth
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Enter feature name"
              />
            </div>

            <div className="feature-grid">
              <div className="form-field">
                <label>
                  Category <span className="required">*</span>
                </label>
                <FormControl fullWidth className="custom-select">
                  <Select
                    value={formData.categoryId || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        categoryId: Number(e.target.value),
                      }))
                    }
                    displayEmpty
                    IconComponent={SelectIcon}
                  >
                    <MenuItem value="">Select</MenuItem>
                    {categories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>

              <div className="form-field">
                <label>
                  Implementation Scope <span className="required">*</span>
                </label>
                <FormControl fullWidth className="custom-select">
                  <Select
                    value={formData.implementationScope}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        implementationScope: e.target.value,
                      }))
                    }
                    displayEmpty
                    IconComponent={SelectIcon}
                  >
                    <MenuItem value="">Select</MenuItem>
                    <MenuItem value="API Only">API Only</MenuItem>
                    <MenuItem value="SDK Only">SDK Only</MenuItem>
                    <MenuItem value="API & SDK Both">API & SDK Both</MenuItem>
                  </Select>
                </FormControl>
              </div>
            </div>

            <div className="feature-grid">
              <div className="form-field toggle-field">
                <label>
                  USP <span className="required">*</span>
                </label>
                <div className="status-toggle-wrapper">
                  <Toggle
                    id="feature-usp"
                    labelText=""
                    labelON=""
                    labelOFF=""
                    toggled={formData.usp}
                    size={ToggleSizes.Mini}
                    onClick={() => setFormData((prev) => ({ ...prev, usp: !prev.usp }))}
                  />
                  <span className="status-text">{formData.usp ? "Yes" : "No"}</span>
                </div>
              </div>

              <div className="form-field toggle-field">
                <label>
                  Feature Status <span className="required">*</span>
                </label>
                <div className="status-toggle-wrapper">
                  <Toggle
                    id="feature-status"
                    labelText=""
                    labelON=""
                    labelOFF=""
                    toggled={formData.status}
                    size={ToggleSizes.Mini}
                    onClick={() =>
                      setFormData((prev) => ({ ...prev, status: !prev.status }))
                    }
                  />
                  <span className="status-text">
                    {formData.status ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 1 && (
          <div className="technology-tab-content">
            <table className="technology-table">
              <colgroup>
                <col className="col-technology" />
                <col className="col-requirements" />
                <col className="col-support" />
              </colgroup>
              <thead>
                <tr>
                  <th>Technology</th>
                  <th>Requirements</th>
                  <th>Support</th>
                </tr>
              </thead>
              <tbody>
                {formData.details.map((row, index) => (
                  <tr key={row.technology}>
                    <td>{row.technology}</td>
                    <td>
                      <Autocomplete
                        multiple
                        disableCloseOnSelect
                        forcePopupIcon
                        popupIcon={<Iconography icon="angleDown" className="feature-select-icon" />}
                        options={requirementOptions}
                        value={row.requirement}
                        onChange={(_, value) => handleSupportRowChange(index, "requirement", value)}
                        className="requirements-autocomplete"
                        renderOption={(props, option, { selected }) => (
                          <li {...props}>
                            <Checkbox size="small" checked={selected} />
                            {option}
                          </li>
                        )}
                        renderTags={(value, getTagProps) =>
                          value.map((option, tagIndex) => (
                            <Chip
                              variant="outlined"
                              label={option}
                              {...getTagProps({ index: tagIndex })}
                            />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder={row.requirement.length ? "" : "Search requirements"}
                          />
                        )}
                      />
                    </td>
                    <td>
                      {/** rotate caret when popover open for this row */}
                      <button
                        type="button"
                        className={`support-dropdown-trigger ${
                          Boolean(supportAnchorEl) && supportEditingRow === index ? "is-open" : ""
                        }`}
                        onClick={(e) => openSupportSelector(e, index, row.support)}
                      >
                        <span>{row.support || "Select"}</span>
                        <Iconography icon="angleDown" className="support-dropdown-icon" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 2 && (
          <div className="description-tab-content">
            <div className="form-field">
              <div className="field-label-row">
                <label>
                  High Level Scenario <span className="required">*</span>
                </label>
                <div className="char-count">{stripHtml(formData.highLevelScenario).length}/1000</div>
              </div>
              <InlineEditor
                id="feature-high-level-scenario"
                placeholder="Enter high level scenario"
                toolBarConfig={editorToolBarConfig}
                editorData={formData.highLevelScenario}
                handleOnChange={(value: string) =>
                  setFormData((prev) => ({ ...prev, highLevelScenario: value }))
                }
              />
            </div>

            <div className="form-field">
              <div className="field-label-row">
                <label>
                  Requirement Description <span className="required">*</span>
                </label>
                <div className="char-count">
                  {stripHtml(formData.requirementDescription).length}/1000
                </div>
              </div>
              <InlineEditor
                id="feature-requirement-description"
                placeholder="Enter requirement description"
                toolBarConfig={editorToolBarConfig}
                editorData={formData.requirementDescription}
                handleOnChange={(value: string) =>
                  setFormData((prev) => ({ ...prev, requirementDescription: value }))
                }
              />
            </div>
          </div>
        )}
      </DialogContent>

      <DialogActions>
        <Button variant="secondary" label="Cancel" onClick={onClose} />
        <Button
          variant="primary"
          label={mode === "edit" ? "Update Feature" : "Add Feature"}
          onClick={handleSubmit}
          disabled={!isFormValid}
        />
      </DialogActions>

      <Popover
        open={Boolean(supportAnchorEl)}
        anchorEl={supportAnchorEl}
        onClose={closeSupportSelector}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        className="feature-support-popover"
      >
        <div className="support-popover-body">
          <FormControlLabel
            control={
              <Radio
                checked={supportType === "supported"}
                onChange={() => setSupportType("supported")}
              />
            }
            label="Supported"
            className={supportType === "supported" ? "active-option" : ""}
          />
          <FormControlLabel
            control={
              <Radio
                checked={supportType === "not-supported"}
                onChange={() => setSupportType("not-supported")}
              />
            }
            label="Not-Supported"
            className={supportType === "not-supported" ? "active-option" : ""}
          />
          <FormControlLabel
            control={
              <Radio checked={supportType === "custom"} onChange={() => setSupportType("custom")} />
            }
            label="Custom"
            className={supportType === "custom" ? "active-option" : ""}
          />

          {supportType === "custom" && (
            <div className="custom-support-section">
              <div className="custom-mode-switch">
                <button
                  type="button"
                  className={customMode === "year" ? "active" : ""}
                  onClick={() => setCustomMode("year")}
                >
                  Year
                </button>
                <button
                  type="button"
                  className={customMode === "full-date" ? "active" : ""}
                  onClick={() => setCustomMode("full-date")}
                >
                  Full date
                </button>
              </div>
              {customMode === "year" ? (
                <>
                  <label>Year</label>
                  <FormControl fullWidth size="small">
                    <Select
                      value={customYear}
                      onChange={(e) => setCustomYear(e.target.value)}
                      displayEmpty
                      IconComponent={SelectIcon}
                    >
                      <MenuItem value="">Select</MenuItem>
                      {yearOptions.map((item) => (
                        <MenuItem key={item} value={item}>
                          {item}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <label>Quarter (Optional)</label>
                  <FormControl fullWidth size="small">
                    <Select
                      value={customQuarter}
                      onChange={(e) => setCustomQuarter(e.target.value)}
                      displayEmpty
                      IconComponent={SelectIcon}
                    >
                      <MenuItem value="">Select</MenuItem>
                      {quarterOptions.map((item) => (
                        <MenuItem key={item} value={item}>
                          {item}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </>
              ) : (
                <>
                  <label>Date</label>
                  <TextField
                    type="date"
                    size="small"
                    fullWidth
                    value={customFullDate}
                    onChange={(e) => setCustomFullDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </>
              )}
            </div>
          )}
        </div>
      </Popover>
    </Dialog>
  );
};

export default FeatureModal;

const stripHtml = (value: string): string =>
  value.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();

const formatInputDateToDisplayDate = (value: string): string => {
  const [year, month, day] = value.split("-");
  if (!year || !month || !day) return "";
  return `${day}/${month}/${year}`;
};

const convertDisplayDateToInputDate = (value: string): string => {
  const [day, month, year] = value.split("/");
  if (!year || !month || !day) return "";
  return `${year}-${month}-${day}`;
};
